import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Timer } from "lucide-react";
import { posts } from "@/data/blog";
import { images } from "@/config/site";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTA } from "@/components/CTA";

export default function Blog() {
  return (
    <>
      <SEO
        title="Painting Advice Blog | North Ember Painting — Delta, BC"
        description="Practical painting advice from North Ember Painting: choosing colours, preparing rooms, exterior maintenance in BC's coastal climate, cabinet painting and hiring a painter in Delta."
        path="/blog"
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Painting Advice", path: "/blog" }])]}
      />
      <PageHero
        eyebrow="Painting advice"
        title="Straightforward guidance from the trade"
        intro="Short, practical articles on getting a painting project right — written from real job-site experience in the Delta area."
        image="swatchCompare"
      />
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, i) => (
              <Reveal key={post.slug} delay={(i % 3) * 70}>
                <Link
                  to={`/blog/${post.slug}`}
                  data-testid={`blog-card-${post.slug}`}
                  className="tilt-card group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:border-ember/40 hover:shadow-xl"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={images[post.image].url} alt={images[post.image].alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-ember">{post.category}</p>
                    <h2 className="mt-2 font-display text-xl font-semibold tracking-tight text-slate-900">{post.title}</h2>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{post.excerpt}</p>
                    <div className="mt-4 flex items-center gap-4 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                        {new Date(post.date + "T12:00:00").toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Timer className="h-3.5 w-3.5" aria-hidden="true" />
                        {post.readTime}
                      </span>
                    </div>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-ember-dark transition-colors duration-200 group-hover:text-ember">
                      Read article <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTA />
    </>
  );
}
